import { usePostHog } from "@posthog/react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { uniq } from "lodash-es";
import {
  ChevronDownIcon,
  PlusIcon,
  ShoppingCartIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Button as AriaButton,
  DialogTrigger,
  DropZone,
  isTextDropItem,
  ListBox,
  ListBoxItem,
  MenuTrigger,
  RadioGroup,
  useDragAndDrop,
} from "react-aria-components";
import { match } from "ts-pattern";
import z from "zod";

import { Button } from "@/components/base/button";
import { Label } from "@/components/base/field";
import { useAppForm } from "@/components/base/form";
import { Menu, MenuItem } from "@/components/base/menu";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { Radio } from "@/components/base/radio-group";
import { ProfileName } from "@/components/profiles/name";
import { ProfilePhoto } from "@/components/profiles/photo";
import { Cart } from "@/components/registrations/cart";
import {
  AllProfilesContext,
  type CartProfile,
  DragContext,
  registrationPageSchema,
  useAllProfiles,
  useAllProfilesProvider,
  useDraggedProfile,
  useIsSeasonOpen,
  useSetDraggedProfile,
} from "@/components/registrations/context";
import { DraggableProfile } from "@/components/registrations/draggable-profile";
import {
  RegistrationFormProvider,
  useRegistrationForm,
  useRegistrationFormHelpers,
} from "@/components/registrations/form-context";
import { RegistrationDivisions } from "@/components/registrations/registration-divisions";
import {
  clearSavedCart,
  loadSavedCart,
} from "@/components/registrations/saved-cart";
import type { PlayerProfile, TshirtSize } from "@/db/schema";
import { DefaultLayout } from "@/layouts/default";
import { useIsMounted } from "@/lib/dom";
import { isDefined } from "@/utils/types";

export const Route = createFileRoute("/account/registrations/")({
  validateSearch: registrationPageSchema,
  beforeLoad: ({
    search: { profiles, memberships, teams },
    context: { viewer },
  }) => {
    if (!viewer) {
      throw redirect({
        to: "/log-in",
        search: {
          next: "/account/registrations",
        },
      });
    }

    const deduped = uniq(profiles);

    if (deduped.length !== profiles.length) {
      throw redirect({
        to: "/account/registrations",
        search: {
          profiles: deduped,
          memberships,
          teams,
        },
      });
    }
  },
  head: () => ({
    meta: [{ title: "Registration" }],
  }),
  component: RouteComponent,
  scripts: () => [
    {
      src: `${import.meta.env.VITE_USAEPAY_BASE_URL}/js/v2/pay.js`,
    },
  ],
});

function useHasSavedCart() {
  const search = Route.useSearch();
  const [savedCart, setSavedCart] = useState(() => loadSavedCart());

  const urlIsEmpty =
    search.profiles.length === 0 &&
    search.memberships.length === 0 &&
    search.teams.length === 0;

  // Re-check on mount (SSR won't have localStorage)
  useEffect(() => {
    setSavedCart(loadSavedCart());
  }, []);

  return urlIsEmpty ? savedCart : null;
}

function RouteComponent() {
  const savedCart = useHasSavedCart();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (savedCart && !dismissed) {
    return (
      <DefaultLayout
        classNames={{
          content: "flex flex-col py-8 space-y-6",
        }}
      >
        <div className="py-8 text-center">
          <h1 className={title()}>Registration</h1>
        </div>

        <div className="mx-auto w-full max-w-lg rounded-lg border border-blue-300 bg-blue-50 p-6">
          <div className="flex items-start gap-3">
            <ShoppingCartIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900">
                You have an in-progress registration
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                Looks like you were in the middle of registering. Would you like
                to pick up where you left off?
              </p>
              <div className="mt-4 flex flex-row gap-3">
                <Button
                  color="primary"
                  size="sm"
                  onPress={() => {
                    navigate({
                      to: "/account/registrations",
                      replace: true,
                      search: savedCart,
                    });
                  }}
                >
                  Resume
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    clearSavedCart();
                    setDismissed(true);
                  }}
                >
                  Start fresh
                </Button>
              </div>
            </div>
            <AriaButton
              onPress={() => {
                clearSavedCart();
                setDismissed(true);
              }}
              className="shrink-0 text-blue-400 hover:text-blue-600"
              aria-label="Dismiss"
            >
              <XIcon className="h-4 w-4" />
            </AriaButton>
          </div>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <RegistrationFormProvider>
      <RegistrationPageContent />
    </RegistrationFormProvider>
  );
}

function RegistrationPageContent() {
  const allProfilesValue = useAllProfilesProvider();
  const [draggedProfile, setDraggedProfile] = useState<CartProfile | null>(
    null,
  );

  return (
    <AllProfilesContext.Provider value={allProfilesValue}>
      <DragContext.Provider value={{ draggedProfile, setDraggedProfile }}>
        <DefaultLayout
          classNames={{
            content: "flex flex-col py-8 space-y-6",
          }}
        >
          <div className="py-8 text-center">
            <h1 className={title()}>Registration</h1>
          </div>

          <div className="mx-auto grid w-full flex-1 grid-cols-6 items-start gap-x-3 gap-y-3 px-3">
            <div className="order-2 col-span-full grid grid-cols-1 rounded-lg bg-white md:order-1 md:col-span-4 md:grid-cols-10">
              <div className="col-span-3 border-r border-gray-200">
                <div className="flex flex-row items-center justify-between px-4 py-3">
                  <span>Your Profiles</span>

                  <AddPlayerForm />
                </div>
                <ProfilesSidebar />
              </div>
              <div className="col-span-7">
                <MembershipsSection />
                <TournamentsSection />
              </div>
            </div>

            <Cart className="order-1 md:order-2" />
          </div>
        </DefaultLayout>
      </DragContext.Provider>
    </AllProfilesContext.Provider>
  );
}

function DraggableProfileList({ profiles }: { profiles: CartProfile[] }) {
  const setDraggedProfile = useSetDraggedProfile();

  const { dragAndDropHooks } = useDragAndDrop({
    getItems(keys) {
      return [...keys].map((key) => {
        const item = profiles.find((p) => p.id === key);
        return {
          "text/plain": `${item?.preferredName || item?.firstName} ${item?.lastName}`,
          profile: JSON.stringify({ id: item?.id }),
        };
      });
    },
    getAllowedDropOperations: () => ["copy"],
    onDragStart(e) {
      const key = [...e.keys][0];
      const profile = profiles.find((p) => p.id === key);
      if (profile) {
        setDraggedProfile(profile);
      }
    },
    onDragEnd() {
      setDraggedProfile(null);
    },
  });

  return (
    <ListBox
      aria-label="Players"
      items={profiles}
      dragAndDropHooks={dragAndDropHooks}
      selectionMode="single"
    >
      {(profile) => (
        <ListBoxItem
          key={profile.id}
          id={profile.id}
          textValue={`${profile.preferredName || profile.firstName} ${profile.lastName}`}
          className="flex cursor-grab flex-row items-center justify-between border-b border-gray-200 p-2 active:cursor-grabbing"
        >
          <DraggableProfile {...profile} draggable={true} showLevel={true} />

          {profile.registrations > 0 && (
            <span className="text-gray-400">({profile.registrations})</span>
          )}
        </ListBoxItem>
      )}
    </ListBox>
  );
}

function ProfilesSidebar() {
  const { yourProfiles, pastTeammates, pastTeammatesQuery } = useAllProfiles();
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    pastTeammatesQuery;

  return (
    <>
      <DraggableProfileList profiles={yourProfiles} />
      {isLoading ? (
        <div className="border-t border-gray-200">
          <div className="px-4 py-3">
            <span className="text-sm text-gray-500">Past Teammates</span>
          </div>
          <div className="px-4 pb-3 text-sm text-gray-400">Loading...</div>
        </div>
      ) : pastTeammates.length === 0 && !hasNextPage ? null : (
        <div className="border-t border-gray-200">
          <div className="px-4 py-3">
            <span className="text-sm text-gray-500">Past Teammates</span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <DraggableProfileList profiles={pastTeammates} />
          </div>
          {hasNextPage && (
            <div className="px-4 py-2">
              <Button
                variant="text"
                size="xs"
                onPress={() => fetchNextPage()}
                isDisabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

const TSHIRT_SIZE_OPTIONS: { value: TshirtSize; label: string }[] = [
  { value: "sm", label: "S" },
  { value: "m", label: "M" },
  { value: "l", label: "L" },
  { value: "xl", label: "XL" },
  { value: "xxl", label: "XXL" },
];

type MembershipDragState =
  | "none"
  | "valid"
  | "already-in-cart"
  | "already-existing";

function DroppableMembershipsList({
  profiles,
  memberships,
  onProfileDrop,
  onProfileRemove,
  onTshirtSizeChange,
  onAddClick,
}: {
  profiles: (PlayerProfile & { registrations: number })[];
  memberships: { profileId: number; tshirtSize?: TshirtSize | null }[];
  onProfileDrop: (profileId: number) => void;
  onProfileRemove: (profileId: number) => void;
  onTshirtSizeChange: (profileId: number, size: TshirtSize) => void;
  onAddClick?: () => void;
}) {
  const isMounted = useIsMounted();
  const [dragState, setDragState] = useState<MembershipDragState>("none");
  const draggedProfile = useDraggedProfile();
  const { allProfiles } = useAllProfiles();

  const membershipProfileIds = memberships.map((m) => m.profileId);

  const getDragStateForProfile = (
    profile: CartProfile | null,
  ): MembershipDragState => {
    if (!profile) return "valid";
    // Can't drop if already in cart memberships
    if (membershipProfileIds.includes(profile.id)) return "already-in-cart";
    // Can't drop if already has active membership
    if (profile.activeMembership !== null) return "already-existing";
    return "valid";
  };

  const isInvalidState = (state: MembershipDragState): boolean =>
    state === "already-in-cart" || state === "already-existing";

  return (
    <DropZone
      className={`rounded-md transition-colors ${
        isInvalidState(dragState)
          ? "bg-red-50"
          : dragState === "valid"
            ? "bg-blue-50"
            : ""
      }`}
      getDropOperation={(types) => (types.has("profile") ? "copy" : "cancel")}
      onDropEnter={() => {
        setDragState(getDragStateForProfile(draggedProfile));
      }}
      onDropExit={() => {
        setDragState("none");
      }}
      onDrop={async (e) => {
        const items = await Promise.all(
          e.items.filter(isTextDropItem).map(async (item) => {
            const text = await item.getText("profile");
            return JSON.parse(text) as { id: number };
          }),
        );

        for (const item of items) {
          const profile = allProfiles.find((p) => p.id === item.id);
          if (getDragStateForProfile(profile ?? null) === "valid") {
            onProfileDrop(item.id);
          }
        }

        setDragState("none");
      }}
    >
      {profiles.length === 0 ? (
        <AriaButton
          onPress={onAddClick}
          isDisabled={!isMounted}
          className={`w-full cursor-pointer rounded-md border-2 border-dashed p-4 text-center text-sm transition-colors ${
            isInvalidState(dragState)
              ? "border-red-400 bg-red-50 text-red-600"
              : dragState === "valid"
                ? "border-blue-500 bg-blue-50 text-blue-600"
                : "border-gray-300 bg-gray-50 text-gray-600 hover:border-gray-400 hover:bg-gray-100 hover:text-gray-700"
          }`}
        >
          {match(dragState)
            .with("already-in-cart", () => "Player already in cart")
            .with(
              "already-existing",
              () => "Player already has an active membership",
            )
            .with("valid", () => "Drop to add membership")
            .otherwise(() => "Drag players here to add memberships...")}
        </AriaButton>
      ) : (
        <div className="flex flex-col gap-2">
          {profiles.map((profile) => {
            const membership = memberships.find(
              (m) => m.profileId === profile.id,
            );
            const currentSize = membership?.tshirtSize;
            const sizeLabel =
              TSHIRT_SIZE_OPTIONS.find((o) => o.value === currentSize)?.label ??
              "Select";

            return (
              <div
                key={profile.id}
                className="flex flex-row items-center justify-between gap-x-4 rounded-md border border-gray-300 bg-gray-100 p-2"
              >
                <div className="flex flex-1 items-center gap-x-4 md:flex-row">
                  <div className="flex flex-1 flex-row items-center gap-x-2">
                    <ProfilePhoto {...profile} />
                    <ProfileName {...profile} />
                  </div>
                  <div className="flex flex-col items-center gap-x-2 md:flex-row">
                    <Label isRequired={true}>T-Shirt size</Label>
                    <MenuTrigger>
                      <AriaButton className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm hover:bg-gray-50">
                        {sizeLabel}
                        <ChevronDownIcon size={14} />
                      </AriaButton>

                      <Menu>
                        {TSHIRT_SIZE_OPTIONS.map((option) => (
                          <MenuItem
                            key={option.value}
                            id={option.value}
                            onAction={() =>
                              onTshirtSizeChange(profile.id, option.value)
                            }
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </MenuTrigger>
                  </div>
                </div>
                <Button
                  variant="text"
                  size="xs"
                  tooltip="Remove membership"
                  onPress={() => onProfileRemove(profile.id)}
                >
                  <Trash2Icon size={16} />
                </Button>
              </div>
            );
          })}

          {dragState !== "none" && (
            <div
              className={`rounded-md border-2 border-dashed p-4 text-center text-sm transition-colors ${
                isInvalidState(dragState)
                  ? "border-red-400 bg-red-50 text-red-600"
                  : "border-blue-500 bg-blue-50 text-blue-600"
              }`}
            >
              {match(dragState)
                .with("already-in-cart", () => "Player already in cart")
                .with(
                  "already-existing",
                  () => "Player already has an active membership",
                )
                .otherwise(() => "Drop to add membership")}
            </div>
          )}
        </div>
      )}
    </DropZone>
  );
}

function AddPlayerForm() {
  const { addProfile } = useRegistrationFormHelpers();

  const [open, setOpen] = useState(false);

  const form = useAppForm({
    defaultValues: {
      profileId: null as number | null,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.state.value !== null) {
          formApi.handleSubmit();
        }
      },
    },
    onSubmit: ({ value: { profileId }, formApi }) => {
      if (profileId) {
        addProfile(profileId);
      }

      setOpen(false);
      formApi.reset();
    },
  });

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button color="primary" variant="text" size="xs">
        <PlusIcon size={12} /> Add
      </Button>
      <Modal>
        <div className="flex flex-col space-y-3 p-3">
          <h2 className={title({ size: "sm" })}>Select Player</h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
          >
            <form.AppField name="profileId">
              {(field) => (
                <field.ProfilePicker
                  label="Player"
                  field={field}
                  selectedProfileIds={
                    field.state.value ? [field.state.value] : []
                  }
                />
              )}
            </form.AppField>
            <form.AppForm>
              <form.Footer>
                <Button slot="close">Cancel</Button>

                {/* <form.SubmitButton>Add</form.SubmitButton> */}
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </DialogTrigger>
  );
}

function AddMembershipForm({
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const registrationForm = useRegistrationForm();
  const { addMembership } = useRegistrationFormHelpers();

  const { allProfiles: profiles } = useAllProfiles();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  const form = useAppForm({
    defaultValues: {
      profileIds: [] as number[],
    },
    validators: {
      onChange: z.object({
        profileIds: z.array(z.number()),
      }),
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        // RadioGroup sets profileIds array directly, ProfilePicker sets profileIds[0]
        const shouldSubmit =
          (fieldApi.name === "profileIds" &&
            Array.isArray(fieldApi.state.value) &&
            fieldApi.state.value.length > 0) ||
          (fieldApi.name === "profileIds[0]" && fieldApi.state.value != null);

        if (shouldSubmit) {
          formApi.handleSubmit();
        }
      },
    },
    onSubmit: ({ value: { profileIds }, formApi }) => {
      if (profileIds.length) {
        for (const profileId of profileIds) {
          addMembership(profileId, null);
        }
      }

      setOpen(false);

      formApi.reset();
    },
  });

  // Get current memberships from the registration form
  const membershipProfileIds = registrationForm
    .getFieldValue("memberships")
    .map((m) => m.profileId);

  const availableProfiles = profiles.filter(
    ({ id, activeMembership }) =>
      activeMembership === null && !membershipProfileIds.includes(id),
  );

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button color="primary" variant="text" size="xs">
        <PlusIcon size={12} /> Add Membership
      </Button>
      <Modal>
        <div className="flex flex-col space-y-3 p-3">
          <h2 className={title({ size: "xs" })}>Add Membership</h2>

          <p>Choose a player to add.</p>

          <form
            className="flex flex-col gap-y-3"
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
          >
            {availableProfiles.length > 0 && (
              <>
                <form.AppField name="profileIds">
                  {(field) => (
                    <RadioGroup
                      value={field.state.value[0]?.toString()}
                      onChange={(value) => {
                        field.handleChange([Number.parseInt(value, 10)]);
                      }}
                      aria-label="Select a player"
                    >
                      {availableProfiles.map((profile) => (
                        <Radio key={profile.id} value={profile.id.toString()}>
                          <ProfilePhoto {...profile} />
                          <ProfileName {...profile} link={false} />
                        </Radio>
                      ))}
                    </RadioGroup>
                  )}
                </form.AppField>
                <div className="flex flex-row items-center space-x-3">
                  <hr className="flex-1 border-gray-300" />
                  <span className="text-lg text-gray-500">OR</span>
                  <hr className="flex-1 border-gray-300" />
                </div>
              </>
            )}
            <form.AppField name="profileIds" mode="array">
              {(field) => (
                <form.AppField name="profileIds[0]">
                  {(subField) => (
                    <field.ProfilePicker
                      label="Search for a player"
                      field={subField}
                      selectedProfileIds={
                        field.state.value[0] ? [field.state.value[0]] : []
                      }
                    />
                  )}
                </form.AppField>
              )}
            </form.AppField>
            <form.AppForm>
              <form.Footer>
                <Button slot="close">Cancel</Button>
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </DialogTrigger>
  );
}

function MembershipsSection() {
  const form = useRegistrationForm();
  const { addMembership, removeMembership, updateTshirtSize } =
    useRegistrationFormHelpers();
  const { allProfiles: profiles } = useAllProfiles();
  const [addMembershipOpen, setAddMembershipOpen] = useState(false);

  return (
    <div className="flex flex-col gap-y-3 border-b border-gray-200 px-4 py-3">
      <div className="flex flex-row items-center justify-between">
        <span>Memberships</span>

        <AddMembershipForm
          isOpen={addMembershipOpen}
          onOpenChange={setAddMembershipOpen}
        />
      </div>
      <form.Subscribe
        selector={(state) => state.values.memberships}
        children={(memberships) => {
          const membershipProfiles = memberships
            .map((item) =>
              profiles.find((profile) => profile.id === item.profileId),
            )
            .filter(isDefined);

          return (
            <DroppableMembershipsList
              profiles={membershipProfiles}
              memberships={memberships}
              onProfileDrop={(profileId) => addMembership(profileId, null)}
              onProfileRemove={removeMembership}
              onTshirtSizeChange={updateTshirtSize}
              onAddClick={() => setAddMembershipOpen(true)}
            />
          );
        }}
      />
    </div>
  );
}

function TournamentsSection() {
  const form = useRegistrationForm();
  const isSeasonOpen = useIsSeasonOpen();
  const [addTournamentOpen, setAddTournamentOpen] = useState(false);

  return (
    <div className="flex flex-col gap-y-3 border-b border-gray-200 px-4 py-3">
      <div className="flex flex-row items-center justify-between">
        <span>Tournaments</span>

        <AddTournamentForm
          isOpen={addTournamentOpen}
          onOpenChange={setAddTournamentOpen}
        />
      </div>

      <form.Subscribe
        selector={(state) => state.values.teams}
        children={(teams) => {
          if (!isSeasonOpen) {
            return (
              <div className="rounded-lg border border-gray-300 bg-gray-100 px-2 py-6 text-center text-gray-500">
                Tournament registration not open.
              </div>
            );
          }

          if (teams.length === 0) {
            return (
              <AriaButton
                onPress={() => setAddTournamentOpen(true)}
                className="w-full cursor-pointer rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-2 py-6 text-center text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                Register for a tournament
              </AriaButton>
            );
          }

          return <RegistrationDivisions />;
        }}
      />
    </div>
  );
}

function AddTournamentForm({
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const registrationForm = useRegistrationForm();
  const { addTeam } = useRegistrationFormHelpers();
  const posthog = usePostHog();

  const selectedDivisionIds = registrationForm
    .getFieldValue("teams")
    .map((t) => t.divisionId);

  const isSeasonOpen = useIsSeasonOpen();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  const form = useAppForm({
    defaultValues: {
      divisionId: null as number | null,
    },
    onSubmit: ({ value: { divisionId }, formApi }) => {
      if (divisionId) {
        addTeam(divisionId);
        posthog.capture("tournament_added_to_cart", {
          division_id: divisionId,
        });
      }

      setOpen(false);
      formApi.reset();
    },
  });

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button
        color="primary"
        variant="text"
        size="xs"
        isDisabled={!isSeasonOpen}
      >
        <PlusIcon size={12} /> Add Tournament
      </Button>
      <Modal>
        <div className="flex flex-col space-y-3 p-3">
          <h2 className={title({ size: "sm" })}>Select Tournament</h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <form.AppField name="divisionId">
              {(field) => (
                <field.DivisionPicker
                  label="Tournament Division"
                  field={field}
                  selectedDivisionIds={selectedDivisionIds}
                />
              )}
            </form.AppField>
            <form.AppForm>
              <form.Footer>
                <Button slot="close">Cancel</Button>

                <form.SubmitButton>Add</form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </DialogTrigger>
  );
}
